import { useQuery } from '@apollo/react-hooks';
import { GET_APPLICATION_VIEW_TABLE_DATA } from './GET_APPLICATION_VIEW_TABLE_DATA.graphql';
import type {
  SelectOption,
  ArrayTableCellData,
} from '../../../../../components';
import type {
  ApplicationViewTableDataItem,
  ApplicationViewTableData,
  RawFundingWithStatus,
} from './types';
import {
  formatDateStringAccurate,
  ASSESSMENT_STATUS_DISPLAY_MAP,
  APPLICATION_STATUS_EXPLANATION_MAP,
  APPLICATION_STATUS_TYPE,
} from '../../../../../utils';
import { identityTypeFullOptions } from '../../../../Signup/components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';
import {
  type FUNDING_STATUS,
  FUNDING_STATUS_DISPLAY_STRING,
} from '../../../../ScholarshipManagement/constants/fundingStatus';
import {
  PROGRAMME_DISPLAY_MAP,
  STUDY_MODE_DISPLAY_MAP,
  PROGRAMME_TYPE_DISPLAY_MAP,
  PROGRAMME_TYPE,
  type STUDY_MODE,
  UNIVERSITY_CATEGORY,
} from '../../ApplicationForm/constants';
import { universityCategoryOptions } from '../../ApplicationForm/ProgrammeChoice/options';
import { PAYMENT_TYPE } from '../../const';
import { ApplicationFeeStatus } from '../../../graphql';
import type { FundingSource } from '../../../../Nomination/components';
import type { GSAssigneeList } from '../../../../Assessment/graphql';
import {
  MONITOR_LIST_TEXT,
  OFFER_STATUS,
  OFFER_STATUS_EXPLANATION_MAP,
  OFFER_STATUS_TEXT,
} from '../../../../Assessment';
import dayjs from 'dayjs';
import { CONDITION_STATUS_TEXT } from '../../ApplicationStatus/Offer/ConditionMeet/lang';

const fundingSourceNameShort = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.ABBV;
const fundingSourceNameFull = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

export const useGetPaginatedApplicationViewTableData = (
  filter?: any,
  sort?: any,
  paginator?: any,
): ApplicationViewTableData => {
  const gqlPaginator = {
    page: paginator?.page,
    limit: paginator?.rowsPerPage,
  };
  const { data, loading, error, refetch } = useQuery(
    GET_APPLICATION_VIEW_TABLE_DATA,
    {
      variables: { filter, sort, paginator: gqlPaginator },
      fetchPolicy: 'cache-and-network',
    },
  );
  const rpgDualPhdPartnerList =
    data?.programmeChoiceMetadata?.rpgJoinPhdPartnerList ?? [];
  const prgJointPhdPartnerList =
    data?.programmeChoiceMetadata?.prgCollabPhdPartnerList ?? [];

  const applicationViewData = data?.applicationViewsPaginated?.data ?? [];
  const applicationViewTotalPages =
    data?.applicationViewsPaginated?.paginator?.total_pages;
  const applicationViewCount =
    data?.applicationViewsPaginated?.paginator?.count;

  const applicationViewTableData = applicationViewData.map(
    (application: any) => {
      const {
        id: applicationId,
        applicationNo,
        applicant,
        programmeChoice,
        hkpfsInformation,
        distinctiveScholarships,
        nomination,
        gsDecision,
        meetConditionCheckModel,
        offerModel,
        qualificationCheckModel,
        visaModel,
        onlineRegistrationModel,
        paymentRecords,
        assessment,
        status: applicationStatus,
      } = application;

      // applicant related information
      const {
        email: applicantEmail,
        name,
        identity,
        nationality: applicantNationality,
      } = applicant ?? {};
      const { givenName, surname } = name ?? {};
      const applicantName: ArrayTableCellData[] = [
        { value: surname as string },
        { value: givenName as string },
      ];
      const {
        identityType: applicantIdentityType,
        isLocal: applicantIsLocalIndicator,
        needStudentVisa: applicantNeedStudentVisaIndicator,
      } = identity ?? {};
      const displayedApplicantIdentityType = identityTypeFullOptions.find(
        ({ value }) => {
          return value === applicantIdentityType;
        },
      )?.label;
      const displayedLocalIndicator = (applicantIsLocalIndicator as boolean)
        ? 'Local'
        : 'NON Local';

      const displayedNeedStudentVisaIndicator =
        (applicantNeedStudentVisaIndicator as boolean)
          ? 'Need Student Visa'
          : 'NOT Need Student Visa';

      const applicantIdentityInfo = `${displayedApplicantIdentityType} - ${displayedLocalIndicator} - ${displayedNeedStudentVisaIndicator}`;

      // admission application
      const {
        entryYear,
        studyMode,
        department,
        programme,
        programmeType,
        partner,
        researchArea,
        researchAreaBackup,
        proposedChiefSupervisor,
        proposedChiefSupervisorBackup,
        supervisorInvitations = [],
      } = programmeChoice ?? {};
      const displayedProgramme = PROGRAMME_DISPLAY_MAP[programme];
      const displayedProgrammeType = PROGRAMME_TYPE_DISPLAY_MAP[programmeType];
      const displayedStudyMode =
        STUDY_MODE_DISPLAY_MAP[studyMode as STUDY_MODE];
      const applyProgramme = [
        { value: `${displayedStudyMode} ${displayedProgramme}` },
        { value: `(${displayedProgrammeType})` },
      ];

      const researchAreas = [
        { value: researchArea },
        { value: researchAreaBackup },
      ];
      const proposedChiefSupervisors = [
        { value: proposedChiefSupervisor },
        { value: proposedChiefSupervisorBackup },
      ];

      const {
        university,
        universityCategory,
        isPolyUHomeUniversity,
        proposedChiefSupervisor: partnerProposedChiefSupervisor,
        partnerUniversityApplicationNo,
      } = partner ?? {};

      let partnerUniversity = university;
      if (programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME) {
        partnerUniversity = getOptionsLabel(rpgDualPhdPartnerList, university);
      } else if (
        programmeType === PROGRAMME_TYPE.COLLABORATIVE_PHD_DEGREE_PROGRAMME
      ) {
        partnerUniversity = getOptionsLabel(prgJointPhdPartnerList, university);
      }

      const partnerUniversityCategory = getOptionsLabel(
        universityCategoryOptions,
        universityCategory,
      );

      let partnerIsPolyUHomeUniversityIndicator = 'N/A';
      if (universityCategory === UNIVERSITY_CATEGORY.OVERSEAS) {
        if (isPolyUHomeUniversity as boolean) {
          partnerIsPolyUHomeUniversityIndicator = 'Yes';
        } else {
          partnerIsPolyUHomeUniversityIndicator = 'No';
        }
      }

      // scholarship application
      const { HKPFSReferenceNumber, validHKPFSApplication } =
        hkpfsInformation ?? {};
      const scholarshipApplications: ArrayTableCellData[] = [];
      if (HKPFSReferenceNumber as boolean) {
        const hkpfs = fundingSourceNameShort.HONGKONG_PHD_FELLOWSHIP_SCHEME;
        const hkpfsFull = fundingSourceNameFull.HONGKONG_PHD_FELLOWSHIP_SCHEME;
        scholarshipApplications.push({ value: hkpfs, tooltips: hkpfsFull });
      }
      distinctiveScholarships?.forEach((item: any) => {
        const { fundingSource } = item;
        const displayedFundingSource =
          fundingSourceNameShort[
            fundingSource as keyof typeof fundingSourceNameShort
          ];
        const displayedFundingSourceFull =
          fundingSourceNameFull[
            fundingSource as keyof typeof fundingSourceNameFull
          ];
        scholarshipApplications.push({
          value: displayedFundingSource,
          tooltips: displayedFundingSourceFull,
        });
      });
      if (!scholarshipApplications.length) {
        scholarshipApplications.push({
          value: 'N/A',
          tooltips: 'No Applied Scholarships/Fellowships',
        });
      }

      // validate HKPFS
      const validHKPFSApplicationIndicator = (validHKPFSApplication as boolean)
        ? '√'
        : '-';
      const hkpfsDisplayInformation = [
        { value: HKPFSReferenceNumber },
        { value: validHKPFSApplicationIndicator },
      ];

      // admission nomination
      const {
        supervisors,
        shouldScholarshipNominationPerformed,
        interviewPanel,
        fundings,
        submission,
        _id: nominationId,
        scholarshipNomination,
      } = nomination ?? {};
      const supervisoryTeam = supervisors?.map((item: any) => {
        const { type, netId, givenName, surname } = item;
        // TODO map type to defined constant
        return { value: `${type}: ${surname} ${givenName} (${netId})` };
      });
      const confirmedChiefSupervisorItem = supervisors?.find((item: any) => {
        // TODO map type to defined constant
        return item.type === 'CHIEF_SUPERVISOR';
      });
      const {
        surname: supervisorSurname,
        givenName: supervisorGivenName,
        netId,
      } = confirmedChiefSupervisorItem ?? {};
      const confirmedChiefSupervisor = (confirmedChiefSupervisorItem as boolean)
        ? [
            { value: `${supervisorSurname} ${supervisorGivenName}` },
            { value: `(${netId})` },
          ]
        : [{ value: 'N/A' }];

      // scholarship nomination
      let nominatedFundings: ArrayTableCellData[] = fundings?.map(
        (item: any) => {
          const { source, status, panelRemark } = item;
          const displayedFundingSource =
            fundingSourceNameShort[
              source as keyof typeof fundingSourceNameShort
            ];
          const displayedFundingSourceFull =
            fundingSourceNameFull[source as keyof typeof fundingSourceNameFull];

          const displayedSourceStatus =
            FUNDING_STATUS_DISPLAY_STRING[
              status as keyof typeof FUNDING_STATUS_DISPLAY_STRING
            ] ?? 'N/A';
          return {
            value: `${displayedFundingSource}: ${displayedSourceStatus}`,
            tooltips: `${displayedFundingSourceFull}: ${displayedSourceStatus}`,
            helperText: panelRemark,
          };
        },
      );

      const rawFundingWithStatus: RawFundingWithStatus[] = fundings?.map(
        (funding: any) => ({
          fundingValue: funding?.source as FundingSource,
          fundingStatus: (funding?.status as FUNDING_STATUS) ?? null,
        }),
      );
      if (!nominatedFundings?.length) {
        nominatedFundings = [];
        nominatedFundings.push({ value: 'N/A' });
      }

      // interviewPanel
      const isInterviewCompleted =
        (shouldScholarshipNominationPerformed as boolean) &&
        interviewPanel?.scholarshipInterviewSubmission;

      // nomination submission information
      const { submitted, lastSubmissionTime } = submission ?? {};

      // only display the submission time when submitted, in case return situation
      const nominationSubmissionTime = (submitted as boolean)
        ? lastSubmissionTime
        : undefined;
      const formattedNominationSubmissionTime =
        formatDateStringAccurate(nominationSubmissionTime) ?? 'N/A';

      // application status
      const displayedApplicationStatus =
        ASSESSMENT_STATUS_DISPLAY_MAP[
          applicationStatus as keyof typeof ASSESSMENT_STATUS_DISPLAY_MAP
        ] ?? applicationStatus;

      // gs work information
      const { assignee: gsAssignee } = gsDecision ?? {};

      // meetCondition
      const {
        status: conditionCheckStatus,
        conditionFulfillingDeadline,
        updatedAt: conditionCheckUpdatedAt,
      } = meetConditionCheckModel ?? {};
      const meetConditionCheckStatus =
        CONDITION_STATUS_TEXT[
          conditionCheckStatus as keyof typeof CONDITION_STATUS_TEXT
        ];
      const meetConditionCheckDeadline =
        formatDateStringAccurate(conditionFulfillingDeadline) ?? 'N/A';
      const meetConditionCheckUpdatedAt =
        formatDateStringAccurate(conditionCheckUpdatedAt) ?? 'N/A';

      // qualification check Status
      const { status: qualificationCheckStatus } =
        qualificationCheckModel ?? {};

      // visa application Status
      const { status: visaApplicationStatus } = visaModel ?? {};

      // commencement Date
      const { commencementDate, studentNumber } = onlineRegistrationModel ?? {};
      const formattedCommencementDate =
        formatDateStringAccurate(commencementDate) ?? 'N/A';
      const applicationStatusExplanation =
        APPLICATION_STATUS_EXPLANATION_MAP[
          applicationStatus as keyof typeof APPLICATION_STATUS_EXPLANATION_MAP
        ] ?? applicationStatus;

      const applicationFeePayment = paymentRecords?.find(
        ({ paymentType }: { paymentType: PAYMENT_TYPE }) => {
          return paymentType === PAYMENT_TYPE.APPLICATION_FEE;
        },
      );
      const registrationFeePaymentDDL = paymentRecords?.find(
        ({ paymentType }: { paymentType: PAYMENT_TYPE }) => {
          return paymentType === PAYMENT_TYPE.REGISTRATION_FEE;
        },
      )?.paymentDeadline;

      // not use paymentDate because of backend can't query exact payment date from SAS
      // use the update time of payment's paid status as the rough application submission time
      const submissionDate =
        applicationFeePayment?.paymentStatus === ApplicationFeeStatus.PAID
          ? applicationFeePayment?.updatedAt
          : undefined;

      const applicationSubmissionTime =
        formatDateStringAccurate(submissionDate) ?? 'N/A';
      const registrationFeePaymentDeadline =
        formatDateStringAccurate(registrationFeePaymentDDL) ?? 'N/A';
      const offerDate = !offerModel?.offerDate
        ? 'N/A'
        : dayjs(offerModel?.offerDate).format('DD/MM/YYYY,HH:mm');
      const offerStatus = offerModel?.status ?? 'N/A';

      const paymentOverdue =
        offerStatus === OFFER_STATUS.APPLICANT_ACCEPTED &&
        applicationStatus === APPLICATION_STATUS_TYPE.OVERDUE;

      const displayedOfferStatus = paymentOverdue
        ? 'Payment Overdue'
        : (OFFER_STATUS_TEXT[offerStatus as keyof typeof OFFER_STATUS_TEXT] ??
          offerStatus);

      const offerStatusExplanation = paymentOverdue
        ? 'Offer accepted, but payment overdue.'
        : (OFFER_STATUS_EXPLANATION_MAP[
            offerStatus as keyof typeof OFFER_STATUS_EXPLANATION_MAP
          ] ?? offerStatus);
      const gsSubmissionTime = assessment?.submissionTime ?? 'N/A';
      const itemData: Omit<ApplicationViewTableDataItem, 'action'> = {
        applicationId,
        applicationNo,
        studentNumber,
        // applicant personal information
        applicantName,
        applicantEmail,
        applicantNationality,
        applicantIdentityInfo,

        // admission application
        entryYear,
        department,
        applyProgramme,
        researchAreas,
        proposedChiefSupervisors,
        supervisorInvitations,

        // partner information for dual and joint PhD
        partnerUniversityCategory,
        partnerUniversity,
        partnerIsPolyUHomeUniversityIndicator,
        partnerProposedChiefSupervisor: partnerProposedChiefSupervisor ?? 'N/A',
        partnerUniversityApplicationNo: partnerUniversityApplicationNo ?? 'N/A',

        // scholarship application
        scholarshipApplications,
        polishStatus: scholarshipNomination?.polishStatus,

        // HKPFS information
        hkpfsDisplayInformation,

        // admission nomination
        supervisoryTeam,
        confirmedChiefSupervisor,

        nominationId,

        // nomination submission information
        nominationSubmissionTime: formattedNominationSubmissionTime,

        // scholarship nomination
        nominatedFundings,
        rawFundingWithStatus,

        // gs decision
        gsAssignee,
        gsSubmissionTime,
        applicationStatus: displayedApplicationStatus,
        applicationStatusExplanation,
        applicationStatusRaw: applicationStatus,

        applicationSubmissionTime,

        isInterviewCompleted,

        meetConditionCheckStatus,
        meetConditionCheckDeadline,
        meetConditionCheckUpdatedAt,

        qualificationCheckStatus,
        visaApplicationStatus,
        commencementDate: formattedCommencementDate,
        programmeCode: offerModel?.programmeCode ?? '',
        offerDate,
        offerStatus: displayedOfferStatus,
        offerStatusExplanation,
        // payment
        registrationFeePaymentDeadline,

        // rawData
        applicationView: application,
      };

      return itemData;
    },
  );

  const gsAssigneeList = (data?.getGSAssigneeList?.assigneeList ??
    []) as GSAssigneeList[];

  const gsAssigneeOption: SelectOption[] = gsAssigneeList.map(assignee => ({
    value: assignee.netId,
    label: `${assignee.displayName} (${assignee.netId})`,
  }));

  gsAssigneeOption.unshift({
    value: MONITOR_LIST_TEXT.UNASSIGNED,
    label: MONITOR_LIST_TEXT.UNASSIGNED,
  });

  const paginatedApplicationViewTableData = {
    data: applicationViewTableData,
    totalPage: applicationViewTotalPages,
    count: applicationViewCount,
  };

  return {
    paginatedApplicationViewTableData,
    gsAssigneeOption,
    loading,
    error,
    refetch,
  };
};
